// ============================================================================
// @alloyhead/core — Configurable Logger
// ============================================================================
// Framework-internal logging that apps can silence, redirect, or filter.
//
// Usage:
//   import { logger, setLogger } from './logger.js';
//
//   logger.log('info message');     // default: console.log
//   logger.warn('warning');         // default: console.warn
//   logger.error('error', err);     // default: console.error
//
//   // Silence all framework logging:
//   setLogger({ log: noop, warn: noop, error: noop });
//
//   // Redirect to custom handler:
//   setLogger({ log: myLog, warn: myWarn, error: myError });
//
//   // Only keep errors:
//   setLogger({ log: noop, warn: noop, error: console.error });
//
// Design:
//   - Module-level singleton — one logger for the entire framework.
//   - Default is console (same behavior as before).
//   - setLogger() can be called at any time (including after init).
//   - Logger methods match console signatures (variadic args).
// ============================================================================
/** No-op function for silencing log levels. */
export const noop = (..._args) => { };
/** Default logger — delegates to console. */
const defaultLogger = {
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
};
/** The active logger instance. */
let activeLogger = defaultLogger;
/**
 * Get the current framework logger.
 * All framework code should use this instead of `console` directly.
 */
export const logger = {
    log(...args) { activeLogger.log(...args); },
    warn(...args) { activeLogger.warn(...args); },
    error(...args) { activeLogger.error(...args); },
};
/**
 * Replace the framework logger.
 * Pass `{ log: noop, warn: noop, error: noop }` to silence all output.
 * Pass `null` to restore the default console logger.
 */
export function setLogger(custom) {
    activeLogger = custom ?? defaultLogger;
}
//# sourceMappingURL=logger.js.map